import { useFormik } from "formik"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { array, boolean, mixed, number, object, string } from "yup"

import { ProjectFormPageProps } from "components/ProjectForm"
import { EProjectFormPages } from "components/ProjectForm/config"
import useIsCpoDeal, { CPO_DEFAULT_CSMS_PARTNER } from "components/ProjectForm/cpoDefaults"
import { setSaasForm } from "components/ProjectForm/ProjectFormSlice"
import { CSMS_PARTNER_OPTIONS } from "enums/dropdownValues"
import {
    useGetAmenitiesQuery,
    useGetCitiesQuery,
    useGetCoBrandedListQuery,
    useGetCountriesQuery,
    useGetCountryStatesQuery,
    useGetCPOQuery,
} from "pages/Common/commonService"
import { DropdownObj } from "pages/Common/commonTypes"
import { useEditFranchiseAgreementFileMutation } from "pages/Onboardings/OnboardingService"
import {
    useLazyGetProjectFormDetailsByStepQuery,
    useUpdateTicketPageDataMutation,
} from "pages/Projects/ProjectServices"
import { TStore } from "store/Store"

export enum ESaasFields {
    // Main Section
    CPO = "cpo",
    CSMS_PARTNER = "csms_partner",
    CPO_NAME = "cpo_name",
    DEAL_AMOUNT = "deal_amount",
    DEAL_AMOUNT_ESC = "deal_amount_esc",
    SAAS_FEE = "saas_fee",
    LICENSE_FEE_AC = "license_fee_ac",
    LICENSE_FEE_DC = "license_fee_dc",

    // Station Details
    STATION_NAME = "station_name",
    AREA = "area",
    COUNTRY = "country",
    STATE = "state",
    CITY = "city",
    PINCODE = "pincode",
    LANDMARK = "landmark",
    LATITUDE = "latitude",
    LONGITUDE = "longitude",
    AMENITIES = "amenities",
    ACCESS_TYPE = "access_type",
    CO_BRANDING_ENABLED = "co_branding_enabled",
    CO_BRANDING_PARTNER = "co_branding_partner",
    OPENING_HOURS = "opening_hours",
    IS_OPEN_24_HOURS = "is_open_24_hours",
    OPEN_TIME = "open_time",
    CLOSE_TIME = "close_time",

    // Location Details
    LOCATION_TYPE = "location_type",
    LOCATION_SUBTYPE = "location_subtype",

    // Charger Details
    CHARGERS = "chargers",

    // Franchise Details
    FRANCHISE = "franchise",
}

export type FranchiseCardValues = {
    franchise_name: string
    saas_fee_ac: string
    saas_fee_dc: string
    host_managed_by: DropdownObj | null
    tenure: string
    state: DropdownObj | null
    city: DropdownObj | null
    agreement_start_date: string
    agreement_end_date: string
    agreement_url: string | null
}

// id matches the backend's franchise_host flag (0/1) directly
export const HOST_MANAGED_BY_OPTIONS: DropdownObj[] = [
    { id: 0, title: "Host (Statiq)" },
    { id: 1, title: "Franchise" },
]

const initialFranchiseValues: FranchiseCardValues = {
    franchise_name: "",
    saas_fee_ac: "",
    saas_fee_dc: "",
    host_managed_by: null,
    tenure: "",
    state: null,
    city: null,
    agreement_start_date: "",
    agreement_end_date: "",
    agreement_url: null,
}

export type SaasFormValues = {
    // Main Section
    [ESaasFields.CPO]: DropdownObj | null
    [ESaasFields.CSMS_PARTNER]: DropdownObj | null
    [ESaasFields.CPO_NAME]: string
    [ESaasFields.DEAL_AMOUNT]: string
    [ESaasFields.DEAL_AMOUNT_ESC]: string
    [ESaasFields.SAAS_FEE]: boolean
    [ESaasFields.LICENSE_FEE_AC]: string
    [ESaasFields.LICENSE_FEE_DC]: string

    // Station Details
    [ESaasFields.STATION_NAME]: string
    [ESaasFields.AREA]: string
    [ESaasFields.COUNTRY]: DropdownObj | null
    [ESaasFields.STATE]: DropdownObj | null
    [ESaasFields.CITY]: DropdownObj | null
    [ESaasFields.PINCODE]: string
    [ESaasFields.LANDMARK]: string
    [ESaasFields.LATITUDE]: string
    [ESaasFields.LONGITUDE]: string
    [ESaasFields.AMENITIES]: DropdownObj[]
    [ESaasFields.ACCESS_TYPE]: DropdownObj | null
    [ESaasFields.CO_BRANDING_ENABLED]: boolean
    [ESaasFields.CO_BRANDING_PARTNER]: DropdownObj | null
    [ESaasFields.OPENING_HOURS]: DropdownObj | null
    [ESaasFields.IS_OPEN_24_HOURS]: boolean
    [ESaasFields.OPEN_TIME]: string
    [ESaasFields.CLOSE_TIME]: string

    // Location Details
    [ESaasFields.LOCATION_TYPE]: DropdownObj | null
    [ESaasFields.LOCATION_SUBTYPE]: DropdownObj | null

    // Charger Details
    [ESaasFields.CHARGERS]: ChargerRow[]

    // Franchise Details
    [ESaasFields.FRANCHISE]: FranchiseCardValues
}

export type ChargerRow = {
    id: string
    charger_id?: number
    name: string
    sku: string
    item_description: string
    ops_power_rating: string
    connector_count: string
    cpo_cost: string
    host_profit: string
    deal_terms: string
    host_rent: string
    host_profit_amt: string
    host_profit_type: string | null
}

const NO_OPTION = { id: 0, title: "No" }
const INDIA_OPTION = { id: 101, title: "India (IN)" }

const initialValues: SaasFormValues = {
    // Main Section
    [ESaasFields.CPO]: null,
    [ESaasFields.CSMS_PARTNER]: null,
    [ESaasFields.CPO_NAME]: "",
    [ESaasFields.DEAL_AMOUNT]: "",
    [ESaasFields.DEAL_AMOUNT_ESC]: "",
    [ESaasFields.SAAS_FEE]: false,
    [ESaasFields.LICENSE_FEE_AC]: "",
    [ESaasFields.LICENSE_FEE_DC]: "",

    // Station Details
    [ESaasFields.STATION_NAME]: "",
    [ESaasFields.AREA]: "",
    [ESaasFields.COUNTRY]: INDIA_OPTION,
    [ESaasFields.STATE]: null,
    [ESaasFields.CITY]: null,
    [ESaasFields.PINCODE]: "",
    [ESaasFields.LANDMARK]: "",
    [ESaasFields.LATITUDE]: "",
    [ESaasFields.LONGITUDE]: "",
    [ESaasFields.AMENITIES]: [],
    [ESaasFields.ACCESS_TYPE]: null,
    [ESaasFields.CO_BRANDING_ENABLED]: false,
    [ESaasFields.CO_BRANDING_PARTNER]: null,
    [ESaasFields.OPENING_HOURS]: { id: 1, title: "24 hours" },
    [ESaasFields.IS_OPEN_24_HOURS]: false,
    [ESaasFields.OPEN_TIME]: "",
    [ESaasFields.CLOSE_TIME]: "",

    // Location Details
    [ESaasFields.LOCATION_TYPE]: null,
    [ESaasFields.LOCATION_SUBTYPE]: null,

    // Charger Details
    [ESaasFields.CHARGERS]: [],

    // Franchise Details
    [ESaasFields.FRANCHISE]: initialFranchiseValues,
}

const dropdownSchema = object({
    id: number().required(),
    title: string().required(),
})

const requiredDropdownSchema = dropdownSchema.nullable().required("This field is required")
const optionalDropdownSchema = dropdownSchema.nullable().default(null)

const validationSchema = object({
    // Main Section
    [ESaasFields.CPO]: requiredDropdownSchema,
    [ESaasFields.CSMS_PARTNER]: requiredDropdownSchema,
    [ESaasFields.CPO_NAME]: string().default(""),
    [ESaasFields.SAAS_FEE]: boolean().required(),
    [ESaasFields.DEAL_AMOUNT]: string().when(ESaasFields.SAAS_FEE, {
        is: true,
        then: schema => schema.required("Amount is required"),
        otherwise: schema => schema.default(""),
    }),
    [ESaasFields.DEAL_AMOUNT_ESC]: string()
        .default("")
        .test("max-100", "Increment percentage cannot be more than 100", value => {
            if (!value) return true
            const num = parseFloat(value)
            return !isNaN(num) && num <= 100
        }),
    // License fees are only required when SaaS Fee = Yes (BUGS-600); otherwise optional.
    [ESaasFields.LICENSE_FEE_AC]: string().when(ESaasFields.SAAS_FEE, {
        is: true,
        then: schema => schema.required("License Fee AC is required"),
        otherwise: schema => schema.default(""),
    }),
    [ESaasFields.LICENSE_FEE_DC]: string().when(ESaasFields.SAAS_FEE, {
        is: true,
        then: schema => schema.required("License Fee DC is required"),
        otherwise: schema => schema.default(""),
    }),

    // Station Details
    [ESaasFields.STATION_NAME]: string().required("Station name is required"),
    [ESaasFields.AREA]: string().default(""),
    [ESaasFields.COUNTRY]: requiredDropdownSchema,
    [ESaasFields.STATE]: requiredDropdownSchema,
    [ESaasFields.CITY]: requiredDropdownSchema,
    [ESaasFields.PINCODE]: string().default(""),
    [ESaasFields.LANDMARK]: string().default(""),
    [ESaasFields.LATITUDE]: string().required("Latitude is required"),
    [ESaasFields.LONGITUDE]: string().required("Longitude is required"),
    [ESaasFields.AMENITIES]: array().of(dropdownSchema).default([]),
    [ESaasFields.ACCESS_TYPE]: requiredDropdownSchema,
    [ESaasFields.CO_BRANDING_ENABLED]: boolean().required(),
    [ESaasFields.CO_BRANDING_PARTNER]: object({
        id: number(),
        title: string(),
    }).when(ESaasFields.CO_BRANDING_ENABLED, {
        is: true,
        then: schema => schema.required("Co-branding partner is required"),
        otherwise: schema => schema.default(null),
    }),
    [ESaasFields.OPENING_HOURS]: requiredDropdownSchema,
    [ESaasFields.IS_OPEN_24_HOURS]: boolean().required(),
    [ESaasFields.OPEN_TIME]: string().when(ESaasFields.OPENING_HOURS, {
        is: (val: any) => val?.title === "Custom",
        then: schema => schema.required("Open time is required"),
        otherwise: schema => schema.default(""),
    }),
    [ESaasFields.CLOSE_TIME]: string().when(ESaasFields.OPENING_HOURS, {
        is: (val: any) => val?.title === "Custom",
        then: schema => schema.required("Close time is required"),
        otherwise: schema => schema.default(""),
    }),

    // Location Details
    [ESaasFields.LOCATION_TYPE]: requiredDropdownSchema,
    [ESaasFields.LOCATION_SUBTYPE]: requiredDropdownSchema,

    // Charger Details - no validation as it's optional
    [ESaasFields.CHARGERS]: mixed().default([]),

    // Franchise Details - visibility/requiredness gated on is_franchise_partner at the render layer
    [ESaasFields.FRANCHISE]: mixed().default(initialFranchiseValues),
})

type SiteRentPayloadValues = {
    host_rent: number | null
    rent_esc: number | null
    rent_esc_amt: number | null
    rent_esc_duration: number | null
}

const initialSiteRentPayloadValues: SiteRentPayloadValues = {
    host_rent: null,
    rent_esc: null,
    rent_esc_amt: null,
    rent_esc_duration: null,
}

const useSaas = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const [showStationDetails, setShowStationDetails] = useState(false)
    const [showLocationDetails, setShowLocationDetails] = useState(false)
    const [showChargerDetails, setShowChargerDetails] = useState(false)
    const [chargerErrors, setChargerErrors] = useState<Record<number, Set<string>>>({})
    const hasPrefilled = useRef(false)
    const siteIdRef = useRef<number | undefined>(undefined)
    const siteRentPayloadRef = useRef<SiteRentPayloadValues>(initialSiteRentPayloadValues)

    const { project_id, saas_form, account_n_sites_form, account_n_sites_form_values } = useSelector(
        (store: TStore) => store.ProjectForm
    )
    const userId = useSelector((store: TStore) => store.verifyLogin.user_id)
    const [updateTicketPageData, { isLoading: isUpdatingPageData }] = useUpdateTicketPageDataMutation()
    const [getProjectFormDetailsByStep] = useLazyGetProjectFormDetailsByStepQuery()

    const isCpoDeal = useIsCpoDeal()
    const isFranchisePartner = !!account_n_sites_form?.is_franchise_partner
    const franchiseNameFromBuyer = account_n_sites_form_values?.accountRoles?.Buyer?.account?.title || ""
    const franchiseIdFromBuyer = account_n_sites_form_values?.accountRoles?.Buyer?.account?.id

    const getInitialValues = (): SaasFormValues => {
        if (!saas_form) return initialValues
        return {
            ...initialValues,
            ...Object.fromEntries(
                Object.entries(saas_form).filter(([_, value]) => value !== null && value !== undefined)
            ),
        } as SaasFormValues
    }

    const formik = useFormik<SaasFormValues>({
        initialValues: getInitialValues(),
        validationSchema,
        validateOnChange: false,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    })

    const { data: amenityList, isFetching: isAmenitiesFetching } = useGetAmenitiesQuery()

    useEffect(() => {
        if (!isFranchisePartner || !franchiseNameFromBuyer) return
        if (formik.values[ESaasFields.FRANCHISE].franchise_name === franchiseNameFromBuyer) return
        formik.setFieldValue(`${ESaasFields.FRANCHISE}.franchise_name`, franchiseNameFromBuyer)
    }, [isFranchisePartner, franchiseNameFromBuyer])

    // SB-8432: Statiq runs the CSMS on its own CPO deals, so the partner is prefilled. Only an empty
    // field is filled, on add and edit alike, leaving a saved or hand-picked partner untouched.
    useEffect(() => {
        if (!isCpoDeal || formik.values[ESaasFields.CSMS_PARTNER]) return

        const option = CSMS_PARTNER_OPTIONS.find(
            partner => partner.title.toLowerCase() === CPO_DEFAULT_CSMS_PARTNER.toLowerCase()
        )
        if (option) formik.setFieldValue(ESaasFields.CSMS_PARTNER, option)
    }, [isCpoDeal, formik.values[ESaasFields.CSMS_PARTNER]])

    const handleFranchiseFieldChange = useCallback(
        (field: keyof FranchiseCardValues, value: string | DropdownObj | null) => {
            formik.setFieldValue(`${ESaasFields.FRANCHISE}.${field}`, value)
        },
        [formik]
    )

    const handleFranchiseStateChange = useCallback(
        (value: DropdownObj | null) => {
            formik.setFieldValue(`${ESaasFields.FRANCHISE}.state`, value)
            formik.setFieldValue(`${ESaasFields.FRANCHISE}.city`, null)
        },
        [formik]
    )

    const [editFranchiseAgreementFile] = useEditFranchiseAgreementFileMutation()

    const handleFranchiseFileUpload = useCallback(
        async (files: FileList | File[]) => {
            if (!files?.length || !franchiseIdFromBuyer) return
            const formData = new FormData()
            formData.append("agreement_file", files[0])
            formData.append("ticket_id", String(franchiseIdFromBuyer))
            formData.append("user_id", String(userId))

            try {
                await editFranchiseAgreementFile(formData).unwrap()
            } catch (err) {
                console.error("Failed to upload franchise agreement", err)
            }
        },
        [editFranchiseAgreementFile, franchiseIdFromBuyer, userId]
    )

    // Agreement End Date auto-calculates from Start Date + Tenure (months, read-only)
    useEffect(() => {
        const { agreement_start_date, tenure } = formik.values[ESaasFields.FRANCHISE]
        if (!agreement_start_date || !tenure) {
            if (formik.values[ESaasFields.FRANCHISE].agreement_end_date) {
                formik.setFieldValue(`${ESaasFields.FRANCHISE}.agreement_end_date`, "")
            }
            return
        }
        const endDate = new Date(agreement_start_date)
        endDate.setMonth(endDate.getMonth() + Number(tenure))
        endDate.setDate(endDate.getDate() - 1)
        const formatted = endDate.toISOString().split("T")[0]
        if (formik.values[ESaasFields.FRANCHISE].agreement_end_date !== formatted) {
            formik.setFieldValue(`${ESaasFields.FRANCHISE}.agreement_end_date`, formatted)
        }
    }, [formik.values[ESaasFields.FRANCHISE].agreement_start_date, formik.values[ESaasFields.FRANCHISE].tenure])

    const fetchAndPrefillSaasData = useCallback(async () => {
        if (!props.isEditMode || !props.projectId || hasPrefilled.current) return

        try {
            const response = await getProjectFormDetailsByStep({
                project_id: props.projectId,
                page_title: EProjectFormPages.SAAS_DETAILS,
            }).unwrap()

            const pageFormData = response?.pages?.[EProjectFormPages.SAAS_DETAILS]
            if (!pageFormData) return

            const pd = pageFormData.page_data || pageFormData
            if (!pd) return

            hasPrefilled.current = true
            siteIdRef.current = pd.site_id
            siteRentPayloadRef.current = {
                host_rent: pd.host_rent != null ? Number(pd.host_rent) : null,
                rent_esc: pd.rent_esc != null ? Number(pd.rent_esc) : null,
                rent_esc_amt: pd.rent_esc_amt != null ? Number(pd.rent_esc_amt) : null,
                rent_esc_duration: pd.rent_esc_duration != null ? Number(pd.rent_esc_duration) : null,
            }

            // Main Section
            if (pd.account_name) formik.setFieldValue(ESaasFields.CPO, { id: pd.cpo_id || 0, title: pd.account_name })
            if (pd.csms_partner) formik.setFieldValue(ESaasFields.CSMS_PARTNER, { id: 0, title: pd.csms_partner })
            if (pd.deal_amount != null) {
                formik.setFieldValue(ESaasFields.SAAS_FEE, true)
                formik.setFieldValue(ESaasFields.DEAL_AMOUNT, String(pd.deal_amount))
            }
            if (pd.deal_amount_esc != null)
                formik.setFieldValue(ESaasFields.DEAL_AMOUNT_ESC, String(pd.deal_amount_esc))
            if (pd.license_fee_ac != null) formik.setFieldValue(ESaasFields.LICENSE_FEE_AC, String(pd.license_fee_ac))
            if (pd.license_fee_dc != null) formik.setFieldValue(ESaasFields.LICENSE_FEE_DC, String(pd.license_fee_dc))

            // Station Details
            if (pd.site_name) formik.setFieldValue(ESaasFields.STATION_NAME, pd.site_name)
            if (pd.address) formik.setFieldValue(ESaasFields.AREA, pd.address)
            if (pd.country_id) formik.setFieldValue(ESaasFields.COUNTRY, { id: pd.country_id, title: pd.country || "" })
            if (pd.state_id) formik.setFieldValue(ESaasFields.STATE, { id: pd.state_id, title: pd.state || "" })
            if (pd.city_id) formik.setFieldValue(ESaasFields.CITY, { id: pd.city_id, title: pd.city || "" })
            if (pd.pincode) formik.setFieldValue(ESaasFields.PINCODE, pd.pincode)
            if (pd.landmark) formik.setFieldValue(ESaasFields.LANDMARK, pd.landmark)
            if (pd.latitude != null) formik.setFieldValue(ESaasFields.LATITUDE, String(pd.latitude))
            if (pd.longitude != null) formik.setFieldValue(ESaasFields.LONGITUDE, String(pd.longitude))
            if (pd.amenities) {
                const amenityIds = Array.isArray(pd.amenities) ? pd.amenities : pd.amenities?.split(",")
                const amenities = amenityIds.map((id: any) => {
                    const numericId = Number(id)
                    const matchedAmenity = amenityList?.find(a => a.amenity_id === numericId)

                    return {
                        id: numericId,
                        title: matchedAmenity?.amenity_name || "",
                    }
                })
                formik.setFieldValue(ESaasFields.AMENITIES, amenities)
            }
            if (pd.access_type) formik.setFieldValue(ESaasFields.ACCESS_TYPE, { id: 0, title: pd.access_type })

            // Co-Branding
            if (pd.is_cobranded) formik.setFieldValue(ESaasFields.CO_BRANDING_ENABLED, !!pd.is_cobranded)
            formik.setFieldValue(ESaasFields.CO_BRANDING_PARTNER, {
                id: pd.cobranded_id,
                title: pd.cobranded || "",
            })

            // Opening Hours
            if (pd.is_open_24_hours != null) {
                formik.setFieldValue(ESaasFields.IS_OPEN_24_HOURS, !!pd.is_open_24_hours)
                formik.setFieldValue(
                    ESaasFields.OPENING_HOURS,
                    pd.is_open_24_hours ? { id: 1, title: "24 hours" } : { id: 0, title: "Custom" }
                )
            }
            if (pd.open_time) formik.setFieldValue(ESaasFields.OPEN_TIME, pd.open_time)
            if (pd.close_time) formik.setFieldValue(ESaasFields.CLOSE_TIME, pd.close_time)

            // Location Details
            if (pd.location_type) formik.setFieldValue(ESaasFields.LOCATION_TYPE, { id: 0, title: pd.location_type })
            if (pd.location_subtype)
                formik.setFieldValue(ESaasFields.LOCATION_SUBTYPE, { id: 0, title: pd.location_subtype })

            if (pd.franchise_id) {
                formik.setFieldValue(ESaasFields.FRANCHISE, {
                    franchise_name: pd.franchise || "",
                    saas_fee_ac: pd.f_saas_ac != null ? String(pd.f_saas_ac) : "",
                    saas_fee_dc: pd.f_saas_dc != null ? String(pd.f_saas_dc) : "",
                    host_managed_by: HOST_MANAGED_BY_OPTIONS.find(o => o.id === pd.franchise_host) || null,
                    tenure: pd.tenure != null ? String(pd.tenure) : "",
                    state: pd.state_id ? { id: pd.state_id, title: pd.state || "" } : null,
                    city: pd.city_id ? { id: pd.city_id, title: pd.city || "" } : null,
                    agreement_start_date: pd.f_agreement_start || "",
                    agreement_end_date: pd.f_agreement_end || "",
                    agreement_url: pd.franchise_agreement || null,
                })
            }

            // Chargers
            const chargers = pageFormData.chargers || pd.chargers
            if (chargers?.length > 0) {
                const chargerRows: ChargerRow[] = chargers.map((c: any) => ({
                    id: c.id || String(Date.now()),
                    charger_id: c.charger_id,
                    name: c.charger_name || c.display_name || "",
                    sku: c.sku_code || c.sku || "",
                    item_description: c.item_description || "",
                    ops_power_rating: c.operational_power_rating ? String(c.operational_power_rating) : "",
                    connector_count: c.connector_count ? String(c.connector_count) : "",
                    cpo_cost: c.price || c.cpo_cost,
                    host_profit: c.host_profit != null ? String(c.host_profit) : "",
                    deal_terms: c.deal_terms != null ? String(c.deal_terms) : "",
                    host_rent: c.host_rent != null ? String(c.host_rent) : "",
                    host_profit_amt: c.host_profit_amt != null ? String(c.host_profit_amt) : "",
                    host_profit_type: c.host_profit_type != null ? String(c.host_profit_type) : "",
                }))
                formik.setFieldValue(ESaasFields.CHARGERS, chargerRows)
                setShowChargerDetails(true)
            }

            setShowStationDetails(true)
            setShowLocationDetails(true)
        } catch (error) {
            console.error("Error fetching SaaS prefill data:", error)
        }
    }, [props.isEditMode, props.projectId, getProjectFormDetailsByStep, formik, amenityList])

    useEffect(() => {
        fetchAndPrefillSaasData()
    }, [fetchAndPrefillSaasData])

    const { data: cpoList, isFetching: isCpoFetching } = useGetCPOQuery()
    const { data: coBrandedList, isFetching: isCoBrandedFetching } = useGetCoBrandedListQuery()
    const { data: countryList, isFetching: isCountriesFetching } = useGetCountriesQuery()

    const selectedCountryId = formik.values[ESaasFields.COUNTRY]?.id
    const selectedStateId = formik.values[ESaasFields.STATE]?.id

    const { data: stateList, isFetching: isStatesFetching } = useGetCountryStatesQuery(
        { country_id: selectedCountryId! },
        { skip: !selectedCountryId }
    )
    const { data: cityList, isFetching: isCitiesFetching } = useGetCitiesQuery(
        { state_id: selectedStateId ? [selectedStateId] : [] },
        { skip: !selectedStateId }
    )

    const selectedFranchiseStateId = formik.values[ESaasFields.FRANCHISE].state?.id
    const { data: franchiseStateList, isFetching: isFranchiseStatesFetching } = useGetCountryStatesQuery(
        { country_id: INDIA_OPTION.id },
        { skip: !isFranchisePartner }
    )
    const { data: franchiseCityList, isFetching: isFranchiseCitiesFetching } = useGetCitiesQuery(
        { state_id: selectedFranchiseStateId ? [selectedFranchiseStateId] : [] },
        { skip: !selectedFranchiseStateId }
    )

    const yesNoOptions = [
        { id: 1, title: "Yes" },
        { id: 0, title: "No" },
    ]

    const coBrandedOptions = useMemo(() => {
        return (
            coBrandedList?.map(coBranded => ({
                id: coBranded.id,
                title: coBranded.name,
            })) || []
        )
    }, [coBrandedList])

    const cpoOptions = useMemo(() => {
        return (
            cpoList?.data?.message
                ?.filter(cpo => cpo.name)
                .map(cpo => ({
                    id: cpo.id,
                    title: cpo.name,
                })) || []
        )
    }, [cpoList])

    const countryOptions = useMemo(() => {
        return (
            countryList?.data?.map(country => ({
                id: country.id,
                title: `${country.name} (${country.shortname})`,
            })) || []
        )
    }, [countryList])

    const stateOptions = useMemo(() => {
        return (
            stateList?.map(state => ({
                id: state.id,
                title: state.name,
            })) || []
        )
    }, [stateList])

    const cityOptions = useMemo(() => {
        return (
            cityList?.map(city => ({
                id: city.id,
                title: city.name,
            })) || []
        )
    }, [cityList])

    const franchiseStateOptions = useMemo(() => {
        return franchiseStateList?.map(state => ({ id: state.id, title: state.name })) || []
    }, [franchiseStateList])

    const franchiseCityOptions = useMemo(() => {
        return franchiseCityList?.map(city => ({ id: city.id, title: city.name })) || []
    }, [franchiseCityList])

    const amenityOptions = useMemo(() => {
        return (
            amenityList?.map(amenity => ({
                id: amenity.amenity_id,
                title: amenity.amenity_name,
            })) || []
        )
    }, [amenityList])

    function hasError(field: ESaasFields) {
        return Boolean(formik.submitCount > 0 && formik.errors[field])
    }

    function handleRadioChange(field: ESaasFields, value: DropdownObj) {
        const boolValue = value.id === 1
        formik.setFieldValue(field, boolValue)
        // Validation only runs on submit, so clear errors that no longer apply once SaaS Fee is No.
        if (field === ESaasFields.SAAS_FEE && !boolValue) {
            formik.setFieldError(ESaasFields.DEAL_AMOUNT, undefined)
            formik.setFieldError(ESaasFields.LICENSE_FEE_AC, undefined)
            formik.setFieldError(ESaasFields.LICENSE_FEE_DC, undefined)
        }
    }

    function getRadioValue(field: ESaasFields): DropdownObj | undefined {
        const value = formik.values[field]
        if (typeof value === "boolean") {
            return value ? yesNoOptions[0] : yesNoOptions[1]
        }
        return undefined
    }

    // Handle country change - reset state and city
    function handleCountryChange(value: DropdownObj | null) {
        formik.setFieldValue(ESaasFields.COUNTRY, value)
        formik.setFieldValue(ESaasFields.STATE, null)
        formik.setFieldValue(ESaasFields.CITY, null)
    }

    // Handle state change - reset city
    function handleStateChange(value: DropdownObj | null) {
        formik.setFieldValue(ESaasFields.STATE, value)
        formik.setFieldValue(ESaasFields.CITY, null)
    }

    const ALWAYS_REQUIRED_CHARGER_FIELDS: (keyof ChargerRow)[] = ["ops_power_rating", "cpo_cost", "deal_terms"]

    // host_rent = Rental, host_profit = Profit Share, host_profit_amt + host_profit_type = Rev Share
    const DEAL_TERMS_REQUIRED_FIELDS: Record<string, (keyof ChargerRow)[]> = {
        rent_rev_or_profit: ["host_rent", "host_profit_amt", "host_profit_type", "host_profit"],
        rent_profit_or_rev: ["host_rent", "host_profit", "host_profit_amt", "host_profit_type"],
        rent_or_rev: ["host_rent", "host_profit_amt", "host_profit_type"],
        rent_or_profit: ["host_rent", "host_profit"],
        rev_or_profit: ["host_profit_amt", "host_profit_type", "host_profit"],
        rent_rev: ["host_rent", "host_profit_amt", "host_profit_type"],
        rent_rev_per_unit: ["host_rent", "host_profit_amt", "host_profit_type"],
        rent: ["host_rent"],
        rev: ["host_profit_amt", "host_profit_type"],
        rev_per_unit: ["host_profit_amt", "host_profit_type"],
        profit: ["host_profit"],
        none: [],
    }

    function getRequiredFieldsForDealTerm(dealTerm: string): Set<string> {
        return new Set<string>([...ALWAYS_REQUIRED_CHARGER_FIELDS, ...(DEAL_TERMS_REQUIRED_FIELDS[dealTerm] || [])])
    }

    // Charger management function - only editing allowed, chargers are prefilled from API
    function handleChargerChange(index: number, field: keyof ChargerRow, value: string) {
        const updatedChargers = [...formik.values[ESaasFields.CHARGERS]]
        updatedChargers[index] = { ...updatedChargers[index], [field]: value }
        formik.setFieldValue(ESaasFields.CHARGERS, updatedChargers)

        setChargerErrors(prev => {
            const currentRowErrors = prev[index]
            if (!currentRowErrors) return prev
            let fields = new Set(currentRowErrors)
            if (value) fields.delete(field)
            if (field === "deal_terms") {
                const requiredFields = getRequiredFieldsForDealTerm(value)
                fields = new Set(Array.from(fields).filter(f => requiredFields.has(f)))
            }
            const updated = { ...prev }
            if (fields.size === 0) {
                delete updated[index]
            } else {
                updated[index] = fields
            }
            return updated
        })
    }

    function handleBulkChargerUpdate(indices: number[], fields: Partial<ChargerRow>) {
        const updatedChargers = [...formik.values[ESaasFields.CHARGERS]]
        indices.forEach(index => {
            updatedChargers[index] = { ...updatedChargers[index], ...fields }
        })
        formik.setFieldValue(ESaasFields.CHARGERS, updatedChargers)

        setChargerErrors(prev => {
            const updated = { ...prev }
            indices.forEach(index => {
                const rowErrors = updated[index]
                if (!rowErrors) return
                let errorFields = new Set(rowErrors)
                Object.entries(fields).forEach(([field, value]) => {
                    if (value) errorFields.delete(field)
                })
                if (fields.deal_terms) {
                    const requiredFields = getRequiredFieldsForDealTerm(fields.deal_terms)
                    errorFields = new Set(Array.from(errorFields).filter(f => requiredFields.has(f)))
                }
                if (errorFields.size === 0) {
                    delete updated[index]
                } else {
                    updated[index] = errorFields
                }
            })
            return updated
        })
    }

    function validateChargers(chargers: ChargerRow[]): boolean {
        const errors: Record<number, Set<string>> = {}
        let hasError = false

        chargers.forEach((charger, index) => {
            const missingFields = new Set<string>()
            const requiredFields = getRequiredFieldsForDealTerm(charger.deal_terms)
            requiredFields.forEach(field => {
                if (!charger[field as keyof ChargerRow]) {
                    missingFields.add(field)
                    hasError = true
                }
            })
            // When percentage is selected, host_profit_amt must be <= 100
            if (charger.host_profit_type === "0" && charger.host_profit_amt && Number(charger.host_profit_amt) > 100) {
                missingFields.add("host_profit_amt")
                hasError = true
            }
            if (missingFields.size > 0) {
                errors[index] = missingFields
            }
        })

        setChargerErrors(errors)
        return !hasError
    }

    function isValidLatitude(lat: string) {
        const num = parseFloat(lat)
        return !isNaN(num) && num >= -90 && num <= 90
    }

    function isValidLongitude(lng: string) {
        const num = parseFloat(lng)
        return !isNaN(num) && num >= -180 && num <= 180
    }

    function handleCoordinatePaste(e: React.ClipboardEvent, field: "latitude" | "longitude") {
        const pastedText = e.clipboardData.getData("text")
        const coordinateMatch = pastedText.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/)

        if (coordinateMatch) {
            e.preventDefault()

            const lat = parseFloat(coordinateMatch[1])
            const long = parseFloat(coordinateMatch[2])

            const roundedLat = Math.round(lat * 1000000) / 1000000
            const roundedLong = Math.round(long * 1000000) / 1000000

            formik.setFieldValue(ESaasFields.LATITUDE, roundedLat.toString())
            formik.setFieldValue(ESaasFields.LONGITUDE, roundedLong.toString())
        }
    }

    async function handleSubmit(values: SaasFormValues) {
        if (values[ESaasFields.CHARGERS].length > 0 && !validateChargers(values[ESaasFields.CHARGERS])) {
            enqueueSnackbar("Please fill all mandatory charger fields", { variant: "error" })
            return
        }

        const payload = {
            page_title: (props.pageData?.title as EProjectFormPages.SAAS_DETAILS) || EProjectFormPages.SAAS_DETAILS,
            project_id: project_id!,
            site: {
                site_id: siteIdRef.current,
                name: values[ESaasFields.STATION_NAME],
                access_type: values[ESaasFields.ACCESS_TYPE]?.title,
                is_cobranded: values[ESaasFields.CO_BRANDING_ENABLED] ? 1 : 0,
                cobranded_id: values[ESaasFields.CO_BRANDING_ENABLED]
                    ? values[ESaasFields.CO_BRANDING_PARTNER]?.id
                    : null,
                is_open_24_hours: values[ESaasFields.OPENING_HOURS]?.id,
                open_time: values[ESaasFields.OPENING_HOURS]?.id ? "00:00" : values[ESaasFields.OPEN_TIME],
                close_time: values[ESaasFields.OPENING_HOURS]?.id ? "23:59" : values[ESaasFields.CLOSE_TIME],
                host_rent: siteRentPayloadRef.current.host_rent,
                rent_esc: siteRentPayloadRef.current.rent_esc,
                rent_esc_amt: siteRentPayloadRef.current.rent_esc_amt,
                rent_esc_duration: siteRentPayloadRef.current.rent_esc_duration,
                amenities: values[ESaasFields.AMENITIES].map(a => a.id),
            },
            cpo: {
                csms_partner: values[ESaasFields.CSMS_PARTNER]?.title,
                name: values?.[ESaasFields.CPO]?.title,
                deal_amount: values[ESaasFields.SAAS_FEE] ? Number(values[ESaasFields.DEAL_AMOUNT]) : 0,
                deal_amount_esc: values[ESaasFields.SAAS_FEE] ? Number(values[ESaasFields.DEAL_AMOUNT_ESC]) : 0,
                license_fee_ac: Number(values[ESaasFields.LICENSE_FEE_AC]),
                license_fee_dc: Number(values[ESaasFields.LICENSE_FEE_DC]),
            },
            address: {
                area: values[ESaasFields.AREA],
                landmark: values[ESaasFields.LANDMARK],
                country_id: values[ESaasFields.COUNTRY]?.id,
                pincode: values[ESaasFields.PINCODE] ? Number(values[ESaasFields.PINCODE]) : null,
                location_type: values[ESaasFields.LOCATION_TYPE]?.title,
                location_subtype: values[ESaasFields.LOCATION_SUBTYPE]?.title,
                state_id: values[ESaasFields.STATE]?.id,
                city_id: values[ESaasFields.CITY]?.id,
                latitude: values[ESaasFields.LATITUDE],
                longitude: values[ESaasFields.LONGITUDE],
            },
            charger: values[ESaasFields.CHARGERS].map(charger => ({
                charger_id: charger.charger_id,
                name: charger.name,
                operational_power_rating: charger.ops_power_rating ? Number(charger.ops_power_rating) : null,
                cpo_cost: charger.cpo_cost ? Number(charger.cpo_cost) : null,
                deal_terms: charger.deal_terms || null,
                host_rent: charger.host_rent ? Number(charger.host_rent) : null,
                host_profit: charger.host_profit ? Number(charger.host_profit) : null,
                host_profit_amt: charger.host_profit_amt ? Number(charger.host_profit_amt) : null,
                host_profit_type: charger.host_profit_type ? Number(charger.host_profit_type) : null,
            })),
            // Franchise object is sent only when the Buyer is flagged as a Franchise Partner
            ...(isFranchisePartner &&
                franchiseIdFromBuyer && {
                franchise: {
                    franchise_id: Number(franchiseIdFromBuyer),
                    saas_fee_ac: values[ESaasFields.FRANCHISE].saas_fee_ac
                        ? Number(values[ESaasFields.FRANCHISE].saas_fee_ac)
                        : null,
                    saas_fee_dc: values[ESaasFields.FRANCHISE].saas_fee_dc
                        ? Number(values[ESaasFields.FRANCHISE].saas_fee_dc)
                        : null,
                    franchise_host: values[ESaasFields.FRANCHISE].host_managed_by?.id ?? null,
                    tenure: values[ESaasFields.FRANCHISE].tenure
                        ? Number(values[ESaasFields.FRANCHISE].tenure)
                        : null,
                    state_id: values[ESaasFields.FRANCHISE].state?.id ?? null,
                    city_id: values[ESaasFields.FRANCHISE].city?.id ?? null,
                    agreement_start_date: values[ESaasFields.FRANCHISE].agreement_start_date || null,
                    agreement_end_date: values[ESaasFields.FRANCHISE].agreement_end_date || null,
                },
            }),
        }

        try {
            await updateTicketPageData(payload as any).unwrap()

            // Save form values to Redux
            dispatch(setSaasForm(values))

            enqueueSnackbar("SaaS details saved successfully", { variant: "success" })
            props.handleNextStep()
        } catch (error) {
            console.error("Error updating SaaS page data:", error)
            enqueueSnackbar("Error saving SaaS details", { variant: "error" })
        }
    }

    return {
        states: {
            formik,
            cpoOptions,
            showStationDetails,
            countryOptions,
            stateOptions,
            cityOptions,
            amenityOptions,
            showLocationDetails,
            showChargerDetails,
            chargerErrors,
            yesNoOptions,
            isUpdatingPageData,
            isStateDisabled: !selectedCountryId,
            isCityDisabled: !selectedStateId,
            coBrandedOptions,
            isCpoFetching,
            isCoBrandedFetching,
            isCountriesFetching,
            isStatesFetching,
            isCitiesFetching,
            isAmenitiesFetching,
            isFranchisePartner,
            franchiseIdFromBuyer,
            franchiseStateOptions,
            franchiseCityOptions,
            isFranchiseStatesFetching,
            isFranchiseCitiesFetching,
        },
        functions: {
            handleSubmit: formik.handleSubmit,
            setShowStationDetails,
            setShowLocationDetails,
            setShowChargerDetails,
            handleFranchiseFieldChange,
            handleFranchiseStateChange,
            handleFranchiseFileUpload,
            hasError,
            handleRadioChange,
            getRadioValue,
            handleCountryChange,
            handleStateChange,
            handleChargerChange,
            handleBulkChargerUpdate,
            handleCoordinatePaste,
            isValidLatitude,
            isValidLongitude,
        },
    }
}

export default useSaas
